import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Project, User } from '@jabberr/core';
import { api, ApiError } from '../lib/api.js';
import { loadPreferences } from '../lib/preferences.js';

export interface SessionState {
  user: User | null;
  loading: boolean;
  /** The signed-in person may manage users (ADMIN_EMAILS); drives the Users page link in the sidebar. */
  admin: boolean;
  projects: Project[];
  refreshProjects: () => Promise<Project[]>;
  setUser: (u: User | null) => void;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): ReactNode {
  const [user, setUser] = useState<User | null>(null);
  const [admin, setAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);

  const refreshProjects = useCallback(async (): Promise<Project[]> => {
    const { projects: list } = await api.projects.list();
    setProjects(list);
    return list;
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { user: me, admin: isAdmin } = await api.me();
        if (!alive) return;
        setUser(me);
        setAdmin(isAdmin);
        await refreshProjects();
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 401)) console.error(err);
        if (alive) {
          setUser(null);
          setAdmin(false);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [refreshProjects]);

  // Preferences follow the person: load them from the server whenever someone signs in.
  useEffect(() => {
    if (user?.id) void loadPreferences(user.id);
  }, [user?.id]);

  return (
    <SessionContext.Provider value={{ user, loading, admin, projects, refreshProjects, setUser }}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}
